import React, { createContext, FC, useState, ReactNode, useRef, useContext } from 'react';
import clsx from 'clsx';
import { addDays, sub, startOfDay } from 'date-fns';
import { useIntl } from 'react-intl';
import Flatpickr from 'react-flatpickr';
import DatePicker from 'react-flatpickr';
import 'flatpickr/dist/flatpickr.css';
type IDateRange = {

  from: Date;
  to: Date;
};

type IDateRangeContext = IDateRange & {
  timezone: string;
};

type IButtonDateRange = {
  label: string;
  slug: string;
};

const getDateRange = (range: string): IDateRange => {
  const now = new Date();
  let from: Date;

  switch (range) {
    case 'year':
      from = sub(now, { months: 11 });
      break;
    case 'month':
      from = sub(now, { months: 1 });
      break;
    case 'week':
      from = sub(now, { days: 6 });
      break;
    case 'day':
      from = now;
      break;
    default:
      from = now;
  }

  return {
    from: startOfDay(from),
    to: startOfDay(now),
  };
};

const buttons: IButtonDateRange[] = [
  { label: 'Year', slug: 'year' },
  { label: 'Month', slug: 'month' },
  { label: 'Week', slug: 'week' },
  { label: 'Day', slug: 'day' }
];

const df = {
  year: 'numeric',
  month: 'long',
  day: 'numeric'
} as Intl.DateTimeFormatOptions;

export const DateRangeContext = createContext<IDateRangeContext>({
  from: new Date(),
  to: new Date(),
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
});

type IDateRangeProviderProps = {
  maxDate?: Date;
  minDate?: Date;
  defaultRange: string;
  children: ReactNode;
  middleContent?: ReactNode
};

export const DateRangeProvider: FC<IDateRangeProviderProps> = ({ children, minDate, maxDate, defaultRange, middleContent }) => {
  const intl = useIntl()
  const [range, setRange] = useState<string>(defaultRange)
  const [dateRange, setDateRange] = useState<IDateRange>(getDateRange(defaultRange))
  const flatpickrRef = useRef<DatePicker>(null)

  return (
    <DateRangeContext.Provider value={{
      from: dateRange.from,
      to: addDays(dateRange.to, 1), // add day to $lt: date
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }}>
      <div className="d-flex flex-stack container-fluid  mb-10">
        <div className="d-flex flex-wrap me-3 flex-column align-items-center">
          <h1 className="page-heading text-dark fw-bold fs-3 my-0">
            <>
              From
              <span className="badge badge-secondary ms-3 me-3">
                {intl.formatDate(dateRange.from, df)}
              </span>
              to
              <span className="badge badge-secondary ms-3 me-3">
                {intl.formatDate(dateRange.to, df)}
              </span>
            </>
          </h1>
        </div>
        {middleContent}
        <div className="d-flex align-items-center gap-2 gap-lg-3">
          {buttons.map((b, i) => (
            <button
              key={`date-btn-${i}`}
              disabled={b.slug === range}
              className={clsx('btn btn-sm btn-color-muted btn-active btn-active-primary me-3', {
                active: b.slug === range
              })}
              onClick={() => {
                flatpickrRef.current?.flatpickr.clear()
                setRange(b.slug)
                setDateRange(getDateRange(b.slug))
              }}
            >
              {b.label}
            </button>
          ))}
          <button
            className={clsx('btn btn-sm btn-color-muted btn-active btn-active-primary me-3', {
              active: range === 'custom'
            })}
            onClick={() => flatpickrRef.current?.flatpickr.open()}
          >
            Custom

            <Flatpickr
              options={{
                mode: 'range',
                altInput: true,
                altInputClass: 'datepicker',
                enableTime: false,
                dateFormat: "Y-m-d",
                minDate,
                maxDate
              }}
              ref={flatpickrRef}
              onClose={(dates: Date[]) => {
                setDateRange({ from: dates[0], to: dates[1] })
                setRange('custom')
                const activeElement = document.activeElement as HTMLInputElement | HTMLTextAreaElement;
                activeElement?.blur()
              }}
            />
          </button>

        </div>
      </div>

      {children}
    </DateRangeContext.Provider>
  )
}

export const useDateRange = () => {
  const context = useContext(DateRangeContext);
  if (context === undefined) {
    throw new Error('useDateRange expects to be in WebsiteProvider');
  }
  return context;
};

export type { IDateRange };

